import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Star, Quote, Play, Pause } from 'lucide-react';
import { testimonials } from '../data/mock';
import { ScrollReveal } from './ScrollAnimations';

const TestimonialsSection = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [direction, setDirection] = useState('next');

  const totalSlides = testimonials.length;

  const nextSlide = useCallback(() => {
    setDirection('next');
    setCurrentIndex((prev) => (prev + 1) % totalSlides);
  }, [totalSlides]);

  const prevSlide = useCallback(() => {
    setDirection('prev');
    setCurrentIndex((prev) => (prev - 1 + totalSlides) % totalSlides);
  }, [totalSlides]);

  const goToSlide = (index) => {
    setDirection(index > currentIndex ? 'next' : 'prev');
    setCurrentIndex(index);
  };

  // Auto-play
  useEffect(() => {
    if (!isAutoPlaying) return;
    const interval = setInterval(nextSlide, 5000);
    return () => clearInterval(interval);
  }, [isAutoPlaying, nextSlide]);

  const currentTestimonial = testimonials[currentIndex];

  return (
    <section id="testimonials" className="relative bg-black py-32 overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 bg-grid-pattern opacity-[0.02]" />
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />
      <div className="absolute top-1/2 right-0 w-[500px] h-[500px] bg-accent/5 rounded-full blur-[200px] -translate-y-1/2" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-violet-500/5 rounded-full blur-[200px]" />

      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <ScrollReveal animation="fadeUp">
            <span className="text-accent text-sm font-medium tracking-wider uppercase mb-4 block">
              Témoignages
            </span>
          </ScrollReveal>
          <ScrollReveal animation="fadeUp" delay={100}>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold text-white mb-6">
              Ce que disent nos{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-accent to-violet-400">
                clients
              </span>
            </h2>
          </ScrollReveal>
          <ScrollReveal animation="fadeUp" delay={200}>
            <p className="text-white/50 text-lg max-w-2xl mx-auto">
              Découvrez les retours de nos clients satisfaits et les résultats concrets obtenus grâce à nos solutions.
            </p>
          </ScrollReveal>
        </div>

        {/* Main Carousel */}
        <ScrollReveal animation="fadeUp" delay={300}>
          <div className="relative">
            {/* Featured Testimonial Card */}
            <div className="bg-gradient-to-br from-white/[0.05] to-white/[0.02] border border-white/[0.08] p-8 md:p-12 relative overflow-hidden">
              {/* Quote Icon */}
              <Quote className="absolute top-8 right-8 w-20 h-20 text-accent/10" />

              <div className="grid lg:grid-cols-[300px_1fr] gap-8 lg:gap-12 items-center">
                {/* Left - Client Photo */}
                <div className="relative">
                  <div className="relative w-48 h-48 md:w-64 md:h-64 mx-auto lg:mx-0">
                    {/* Glow effect */}
                    <div className="absolute inset-0 bg-accent/20 rounded-full blur-2xl" />
                    
                    {/* Photo container */}
                    <div className="relative w-full h-full rounded-full overflow-hidden border-2 border-accent/30">
                      <img
                        src={currentTestimonial.image}
                        alt={currentTestimonial.name}
                        className="w-full h-full object-cover transition-transform duration-700"
                        key={currentTestimonial.id}
                      />
                    </div>

                    {/* Result badge */}
                    <div className="absolute -bottom-2 -right-2 bg-accent text-black px-4 py-2 text-sm font-bold">
                      {currentTestimonial.results}
                    </div>
                  </div>
                </div>

                {/* Right - Content */}
                <div className="text-center lg:text-left">
                  {/* Stars */}
                  <div className="flex justify-center lg:justify-start gap-1 mb-6">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        size={20}
                        className={`${
                          i < currentTestimonial.rating
                            ? 'text-accent fill-accent'
                            : 'text-white/20'
                        }`}
                      />
                    ))}
                  </div>

                  {/* Quote */}
                  <blockquote className="text-xl md:text-2xl text-white/90 leading-relaxed mb-8 font-light italic">
                    "{currentTestimonial.text}"
                  </blockquote>

                  {/* Author */}
                  <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                    <div>
                      <div className="text-white font-semibold text-lg">
                        {currentTestimonial.name}
                      </div>
                      <div className="text-white/50 text-sm">
                        {currentTestimonial.role} • {currentTestimonial.company}
                      </div>
                    </div>
                    <div className="hidden lg:block w-px h-8 bg-white/10 mx-4" />
                    <div className="px-3 py-1 bg-accent/10 text-accent text-sm border border-accent/20 w-fit mx-auto lg:mx-0">
                      {currentTestimonial.project}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Navigation Arrows */}
            <button
              onClick={prevSlide}
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2 lg:-translate-x-full w-12 h-12 bg-white/5 border border-white/10 text-white/60 hover:bg-accent hover:text-black hover:border-accent flex items-center justify-center transition-all duration-300 z-10"
            >
              <ChevronLeft size={24} />
            </button>
            <button
              onClick={nextSlide}
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 lg:translate-x-full w-12 h-12 bg-white/5 border border-white/10 text-white/60 hover:bg-accent hover:text-black hover:border-accent flex items-center justify-center transition-all duration-300 z-10"
            >
              <ChevronRight size={24} />
            </button>
          </div>
        </ScrollReveal>

        {/* Dots Navigation & Auto-play Control */}
        <div className="flex items-center justify-center gap-6 mt-8">
          {/* Dots */}
          <div className="flex items-center gap-2">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`transition-all duration-300 ${
                  index === currentIndex
                    ? 'w-8 h-2 bg-accent'
                    : 'w-2 h-2 bg-white/20 hover:bg-white/40'
                }`}
              />
            ))}
          </div>

          {/* Auto-play Toggle */}
          <button
            onClick={() => setIsAutoPlaying(!isAutoPlaying)}
            className={`w-10 h-10 flex items-center justify-center border transition-all duration-300 ${
              isAutoPlaying
                ? 'bg-accent/10 border-accent/30 text-accent'
                : 'bg-white/5 border-white/10 text-white/50'
            }`}
          >
            {isAutoPlaying ? <Pause size={16} /> : <Play size={16} />}
          </button>
        </div>

        {/* Mini Testimonials Grid */}
        <ScrollReveal animation="fadeUp" delay={400}>
          <div className="grid md:grid-cols-3 gap-4 mt-16">
            {testimonials
              .filter((_, i) => i !== currentIndex)
              .slice(0, 3)
              .map((testimonial, index) => (
                <button
                  key={testimonial.id}
                  onClick={() => goToSlide(testimonials.findIndex(t => t.id === testimonial.id))}
                  className="group p-6 bg-white/[0.02] border border-white/[0.05] hover:border-accent/30 text-left transition-all duration-500"
                >
                  <div className="flex items-center gap-4 mb-4">
                    <img
                      src={testimonial.image}
                      alt={testimonial.name}
                      className="w-12 h-12 rounded-full object-cover border border-white/10 group-hover:border-accent/50 transition-colors"
                    />
                    <div>
                      <div className="text-white font-medium text-sm group-hover:text-accent transition-colors">
                        {testimonial.name}
                      </div>
                      <div className="text-white/40 text-xs">
                        {testimonial.company}
                      </div>
                    </div>
                  </div>
                  <p className="text-white/50 text-sm line-clamp-2 group-hover:text-white/70 transition-colors">
                    "{testimonial.text}"
                  </p>
                  <div className="flex items-center gap-1 mt-3">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        size={12}
                        className="text-accent/60 fill-accent/60"
                      />
                    ))}
                  </div>
                </button>
              ))}
          </div>
        </ScrollReveal>

        {/* Stats Bar */}
        <ScrollReveal animation="fadeUp" delay={500}>
          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { value: '100+', label: 'Clients satisfaits' },
              { value: '4.9/5', label: 'Note moyenne' },
              { value: '98%', label: 'Recommandent' },
              { value: '24h', label: 'Temps de réponse' },
            ].map((stat, index) => (
              <div
                key={index}
                className="text-center p-6 bg-white/[0.02] border border-white/[0.05]"
              >
                <div className="text-2xl md:text-3xl font-bold text-accent mb-1">
                  {stat.value}
                </div>
                <div className="text-white/50 text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
};

export default TestimonialsSection;
